import difference from 'lodash/difference';
import sortBy from 'lodash/sortBy';

import {
    ArticleCollectionIds,
    type ArticleCollections,
    CollectionContentTypes,
    type IssuePageSet,
    type IUserCollection,
    type PageSetCollections,
} from '@pressreader/social-types';
import { createSimpleCache } from '@pressreader/utils';

import { collectionChangeSubscription, CollectionEvent } from './subscriptions/collections';
import {
    createUserCollection,
    getArticleCollectionsData,
    getCollectionFeed,
    getCollections,
    getCollectionsOf as apiGetCollectionsOf,
    getLinkedCollections as apiGetLinkedCollections,
    getPageSetCollectionsData,
    removeUserCollection,
    updateUserCollection,
    upsertArticleCollectionsData,
} from './api';
import { isCollectionNotEmpty } from './collections.utils';
import { mapArticleCollections, mapPageSetCollections, sortAndMapCollections } from './mappers';
import { getIssuePageSets, getPageSet } from './pageset.service';
import type { ManageUserCollectionDto, WithCollectionsDto } from './types';

const cache = createSimpleCache({ expire: 5 * 60 * 1000 });

/** Clears the in-memory cache for all user collections metadata. */
function clearCollectionsMetadataCache() {
    cache.clear();
}

function clearCollectionsMetadataCacheAndNotify(event: CollectionEvent, data: string) {
    clearCollectionsMetadataCache();
    collectionChangeSubscription.next({ event, data });
}

/**
 * Invalidates the cached metadata for a user collection and notifies all subscribers of an update event.
 *
 * Useful when a bookmark has been added to a sharing collection and the collection's metadata
 * (e.g. item count) needs to reflect the change immediately without waiting for cache expiry.
 *
 * @param collectionId - The ID of the collection that was updated. Passed to subscribers as the event payload.
 */
function invalidateUserCollectionsMetadata(collectionId = '') {
    clearCollectionsMetadataCacheAndNotify(CollectionEvent.update, collectionId);
}

async function getUserCollections() {
    const dto = await getCollections();
    return await sortAndMapCollections(dto.items);
}

async function getCollectionsOf(profileId: string, force?: boolean): Promise<IUserCollection[]> {
    if (!profileId) {
        throw new Error('profileId not specified');
    }

    if (!force) {
        const promise = cache.get(profileId);
        if (promise) {
            return promise;
        }
    }
    const promise = apiGetCollectionsOf(profileId).then(data => sortAndMapCollections(data.items));
    cache.set(profileId, promise);
    return promise;
}

async function addCollection(data: ManageUserCollectionDto) {
    if (!data) {
        throw new Error('context not specified');
    }
    const collection = await createUserCollection(data);
    clearCollectionsMetadataCacheAndNotify(CollectionEvent.create, collection.id);
    return collection;
}

async function removeCollection(collectionId: string) {
    if (!collectionId) {
        throw new Error('collectionId not specified');
    }
    await removeUserCollection(collectionId);
    clearCollectionsMetadataCacheAndNotify(CollectionEvent.delete, collectionId);
}

async function updateCollection(collectionId: string, data: ManageUserCollectionDto) {
    if (!collectionId) {
        throw new Error('collectionId not specified');
    }
    await updateUserCollection(collectionId, data);
    clearCollectionsMetadataCacheAndNotify(CollectionEvent.update, collectionId);
}

async function getLinkedCollections(cid: string) {
    if (!cid) {
        return Promise.reject('cid not specified');
    }
    const cacheKey = `linked_${cid}`;
    const result = cache.get(cacheKey);
    if (result) {
        return Promise.resolve(result);
    }
    const items = await apiGetLinkedCollections(cid);
    const collections = sortBy(items, 'orderKey').map(item =>
        Object.freeze({
            id: item.linkedCollection.id,
            name: item.linkedCollection.name,
            webName: item.linkedCollection.webName,
            contentCount: item.contentCount || '',
            profileId: item.linkedCollectionOwner.profileName,
        }),
    );
    cache.set(cacheKey, collections);
    return collections;
}

async function getArticleCollections(articleId: string): Promise<ArticleCollections> {
    const itemCollections = await getArticleBookmarkCollections(articleId);
    return {
        ...itemCollections,
        articleId,
        collectionContent: CollectionContentTypes.article,
    };
}

async function getPageSetCollections(pageSetId: string): Promise<PageSetCollections & { pageSet: IssuePageSet }> {
    const [pageSet, itemCollections] = await Promise.all([getPageSet(pageSetId), getPageSetCollectionsData(pageSetId)]);
    return {
        ...mapPageSetCollections(pageSetId, itemCollections),
        pageSet,
        collectionContent: CollectionContentTypes.pageSet,
        pageSetId,
    };
}

// filter pagesets that contain selected pages. e.g. - input [2, 3] should return [[1,2,3], [2,3,4], ...]
async function getFilteredPageSets(issueKey: string, pages: number[]) {
    const issuePageSets = await getIssuePageSets(issueKey);
    const filteredPageSets = issuePageSets.filter(item => difference(pages, item.pages).length === 0);
    const promises = filteredPageSets.map(pageSet =>
        getPageSetCollectionsData(pageSet.id).then(itemCollection => {
            return {
                ...itemCollection,
                pageSet,
            };
        }),
    );
    return Promise.all(promises);
}

async function getArticleBookmarkCollections(articleId: string): Promise<ArticleCollections> {
    const dto: WithCollectionsDto = await getArticleCollectionsData(articleId);
    const model: ArticleCollections = mapArticleCollections(articleId, dto);

    return model;
}

async function getPageSetBookmarkCollections(pageSetIds: string[]): Promise<Array<PageSetCollections>> {
    const requests = pageSetIds.map(getPageSetCollectionsData);
    const pageSetCollectionDtos: WithCollectionsDto[] = await Promise.all(requests);
    const models = pageSetCollectionDtos.map((psDto, idx) => mapPageSetCollections(pageSetIds[idx], psDto)).filter(isCollectionNotEmpty);

    return models;
}

async function upsertArticleCollections(articleId: string, collectionsData: ArticleCollectionIds) {
    await upsertArticleCollectionsData(articleId, collectionsData);
    return mapArticleCollections(articleId, collectionsData);
}

export {
    addCollection,
    getArticleBookmarkCollections,
    getArticleCollections,
    getCollectionFeed,
    getCollectionsOf,
    getFilteredPageSets,
    getLinkedCollections,
    getPageSetBookmarkCollections,
    getPageSetCollections,
    getUserCollections,
    invalidateUserCollectionsMetadata,
    removeCollection,
    updateCollection,
    upsertArticleCollections,
};
